<template>
  <div class="table-body">
    <div v-if="!layerSelected" class="empty-selection">
      Seleccione una Infraestructura Vulnerable
    </div>
    <div v-else-if="!selectedLayerFeatures || selectedLayerFeatures.length === 0" class="empty-selection">
      No hay datos disponibles para esta capa.
    </div>
    <div v-else class="table-container">
      <div v-if="extraKpis.length || tableKpis.length" class="kpi-row">
        <div v-for="(kpi, index) in extraKpis" :key="'extra-' + index" class="kpi-card">
          <span class="kpi-value">{{ kpi.value }}</span>
          <span class="kpi-label">{{ kpi.label }}</span>
        </div>
        <div v-for="(kpi, index) in tableKpis" :key="index" class="kpi-card">
          <span class="kpi-value">{{ kpi.value.toLocaleString('es-CL') }}</span>
          <span class="kpi-label">{{ kpi.label }}</span>
        </div>
      </div>
      <div class="table-actions">
        <input
          type="text"
          v-model="searchQuery"
          class="table-search-input"
          placeholder="Buscar en la tabla…"
          aria-label="Buscar en la tabla"
        >

        <div class="column-toggler-container" v-click-outside="closeInfoPanel">
          <button
            type="button"
            class="column-toggle-btn"
            @click="showInfoPanel = !showInfoPanel"
            title="Ver desglose por región y buffer"
            :aria-expanded="showInfoPanel"
          >
            <font-awesome-icon icon="info-circle" /> Resumen
          </button>
          <div v-if="showInfoPanel" class="column-dropdown shadow-sm info-panel">
            <div class="column-dropdown-header">
              {{ layerSelectedName || "Desglose de la capa" }}
            </div>
            <div class="column-dropdown-body">
              <template v-if="infoByRegion.length">
                <p class="info-panel-subtitle">N° de elementos por Región</p>
                <table class="info-panel-table">
                  <tbody>
                    <tr v-for="row in infoByRegion" :key="row.name">
                      <td>{{ row.name }}</td>
                      <td>{{ row.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <template v-if="infoByBuffer.length">
                <p class="info-panel-subtitle">{{ bufferBreakdownLabel }}</p>
                <table class="info-panel-table">
                  <tbody>
                    <tr v-for="row in infoByBuffer" :key="row.name">
                      <td>{{ row.name }}</td>
                      <td>{{ row.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <template v-if="infoByHour.length">
                <p class="info-panel-subtitle">N° de elementos por Hora</p>
                <table class="info-panel-table">
                  <tbody>
                    <tr v-for="row in infoByHour" :key="row.name">
                      <td>{{ row.name }}</td>
                      <td>{{ row.count }}</td>
                    </tr>
                  </tbody>
                </table>
              </template>
              <p v-if="!infoByRegion.length && !infoByBuffer.length && !infoByHour.length" class="info-panel-empty">
                No hay desglose disponible para esta capa.
              </p>
            </div>
          </div>
        </div>

        <button
          type="button"
          class="column-toggle-btn"
          @click="downloadTableCsv"
          title="Descargar tabla como CSV"
        >
          <font-awesome-icon icon="download" /> CSV
        </button>

        <div class="column-toggler-container" v-click-outside="closeColumnMenu">
          <button
            type="button"
            class="column-toggle-btn"
            @click="showColumnMenu = !showColumnMenu"
            title="Mostrar u ocultar columnas"
            :aria-expanded="showColumnMenu"
          >
            <font-awesome-icon icon="list-alt" /> Columnas
          </button>
          <div v-if="showColumnMenu" class="column-dropdown shadow-sm">
            <div class="column-dropdown-header">
              Columnas visibles
            </div>
            <div class="column-dropdown-body">
              <label class="column-checkbox column-checkbox--all">
                <input type="checkbox" :checked="areAllColumnsVisible" @change="toggleAllColumns">
                Seleccionar Todas
              </label>
              <label v-for="col in tableColumns" :key="col.name" class="column-checkbox">
                <input type="checkbox" :value="col.name" :checked="!hiddenColumns.includes(col.name)" @change="toggleColumn(col.name)">
                {{ col.label }}
              </label>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!filteredRows.length" class="empty-selection">
        No hay filas que coincidan con la búsqueda.
      </div>
      <div v-else class="table-responsive">
        <table class="table table-striped table-bordered table-hover custom-table">
          <thead>
            <tr>
              <th v-for="col in visibleColumns" :key="col.name">{{ col.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in filteredRows" :key="item.originalIndex" @click="zoomToFeature(item.originalIndex)" class="clickable-row" title="Clic para centrar en el mapa">
              <td v-for="col in visibleColumns" :key="col.name">{{ item.row[col.name] || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapService } from '@/services/map/mapService.js';
import { fromLonLat } from 'ol/proj';
import { attributesTableService } from '@/services/layers/attributesTable.js';
import { configuredColumns, isTechnicalResultKey } from '@/services/layers/fieldsConfig.js';

const csvEscape = (value) => {
  const str = value == null ? "" : String(value);
  return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

// columns: [{ name, label }] — el encabezado del CSV usa el alias configurado
// (mismo que la tabla) y los valores se leen por el nombre crudo de la fila.
const jsonToCsv = (rows, columns) => {
  const lines = [columns.map((column) => csvEscape(column.label)).join(",")];
  for (const row of rows) {
    lines.push(columns.map((column) => csvEscape(row[column.name])).join(","));
  }
  return lines.join("\r\n");
};

export default {
  name: "GeoprocessingTable",
  props: {
    layerSelected: {
      type: [String, Number],
      default: ""
    },
    resultsTable: {
      type: Object,
      default: () => ({})
    },
    rawResults: {
      type: Array,
      default: () => []
    },
    extraKpis: {
      type: Array,
      default: () => []
    },
    layerSelectedName: {
      type: String,
      default: ""
    },
    graphType: {
      type: String,
      default: ""
    }
  },
  data() {
    return {
      hiddenColumns: [],
      showColumnMenu: false,
      showInfoPanel: false,
      searchQuery: ""
    }
  },
  computed: {
    selectedLayerFeatures() {
      if (!this.layerSelected || !this.resultsTable) return [];
      return this.resultsTable[this.layerSelected] || [];
    },
    // Configuración de Campos de la capa seleccionada (QA-207): el backend
    // ecoa en cada resultado la capa tal como se le envió (Geoprocessings.vue
    // incluye `campos`), así que se recupera desde rawResults.
    selectedLayerCampos() {
      if (!this.layerSelected || !this.rawResults) return null;
      const item = this.rawResults.find(r => r.layer && String(r.layer.id) === String(this.layerSelected));
      return (item && item.layer && item.layer.campos) || null;
    },
    tableColumns() {
      if (!this.selectedLayerFeatures || this.selectedLayerFeatures.length === 0) return [];
      const keys = new Set();
      this.selectedLayerFeatures.forEach(row => {
          Object.keys(row).forEach(k => keys.add(k));
      });
      // QA-207: visible/order/alias de la configuración de Campos mandan
      // sobre las keys crudas; las keys sin config (como "Radio (m)") se
      // conservan, salvo los identificadores internos del servicio.
      const columns = configuredColumns(
        Array.from(keys).filter(key => !isTechnicalResultKey(key)),
        this.selectedLayerCampos
      );
      // El backend agrega "Radio (m)"/"Hora" al final de cada fila; son la
      // columna más relevante para leer la tabla, así que van primero.
      const radiusIndex = columns.findIndex(col => col.name === "Radio (m)");
      if (radiusIndex > 0) {
        columns.unshift(columns.splice(radiusIndex, 1)[0]);
      }
      const hourIndex = columns.findIndex(col => col.name === "Hora");
      if (hourIndex > 0) {
        columns.unshift(columns.splice(hourIndex, 1)[0]);
      }
      return columns;
    },
    visibleColumns() {
      return this.tableColumns.filter(col => !this.hiddenColumns.includes(col.name));
    },
    areAllColumnsVisible() {
      return this.hiddenColumns.length === 0;
    },
    tableKpis() {
      if (!this.selectedLayerFeatures.length) return [];
      const kpis = [
        { label: "Elementos encontrados", value: this.selectedLayerFeatures.length },
      ];

      if (this.regionKey) {
        const regions = new Set(
          this.selectedLayerFeatures
            .map(row => row[this.regionKey])
            .filter(value => value !== undefined && value !== null && value !== "")
        );
        if (regions.size > 0) {
          const label = regions.size === 1 ? "Región involucrada" : "Regiones involucradas";
          kpis.push({ label, value: regions.size });
        }
      }

      const radiusValues = new Set(
        this.selectedLayerFeatures
          .map(row => row["Radio (m)"])
          .filter(value => value !== undefined && value !== null)
      );
      if (radiusValues.size > 0) {
        const label = radiusValues.size === 1 ? "Radio analizado" : "Radios analizados";
        kpis.push({ label, value: radiusValues.size });
      }

      const hourValues = new Set(
        this.selectedLayerFeatures
          .map(row => row["Hora"])
          .filter(value => value !== undefined && value !== null)
      );
      if (hourValues.size > 0) {
        const label = hourValues.size === 1 ? "Hora analizada" : "Horas analizadas";
        kpis.push({ label, value: hourValues.size });
      }

      return kpis;
    },
    regionKey() {
      const column = this.tableColumns.find(col =>
        col.name.toLowerCase().includes("regi") || col.label.toLowerCase().includes("regi")
      );
      return column ? column.name : null;
    },
    // Desglose detallado del botón "Información" (QA-160 ampliado): mismo
    // criterio que tableKpis, pero contando filas por categoría en vez de
    // solo el total.
    infoByRegion() {
      if (!this.regionKey) return [];
      const counts = new Map();
      this.selectedLayerFeatures.forEach(row => {
        const value = row[this.regionKey];
        if (value === undefined || value === null || value === "") return;
        counts.set(value, (counts.get(value) || 0) + 1);
      });
      return Array.from(counts.entries()).map(([name, count]) => ({ name, count }));
    },
    bufferBreakdownLabel() {
      return this.graphType === "InfluenciaRadial" ? "N° de elementos por Radio" : "N° de elementos por Buffer";
    },
    infoByBuffer() {
      const counts = new Map();
      this.selectedLayerFeatures.forEach(row => {
        const radius = row["Radio (m)"];
        if (radius === undefined || radius === null) return;
        counts.set(radius, (counts.get(radius) || 0) + 1);
      });
      if (counts.size <= 1) return [];
      return Array.from(counts.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([radius, count]) => ({ name: `${radius} m`, count }));
    },
    infoByHour() {
      const counts = new Map();
      this.selectedLayerFeatures.forEach(row => {
        const hour = row["Hora"];
        if (hour === undefined || hour === null) return;
        counts.set(hour, (counts.get(hour) || 0) + 1);
      });
      return Array.from(counts.entries())
        .sort((a, b) => a[0] - b[0])
        .map(([hour, count]) => ({ name: `Hora ${hour}`, count }));
    },
    // Conserva el índice ORIGINAL de cada fila (el que usa zoomToFeature
    // para ubicarla dentro de rawResults) aunque la búsqueda reordene o
    // reduzca la lista visible.
    filteredRows() {
      const rows = this.selectedLayerFeatures.map((row, originalIndex) => ({ row, originalIndex }));
      const query = this.searchQuery.trim().toLowerCase();
      if (!query) return rows;
      return rows.filter(({ row }) =>
        this.visibleColumns.some(col => String(row[col.name] ?? "").toLowerCase().includes(query))
      );
    }
  },
  watch: {
    layerSelected() {
      this.hiddenColumns = [];
      this.showColumnMenu = false;
      this.showInfoPanel = false;
      this.searchQuery = "";
    }
  },
  methods: {
    toggleColumn(col) {
      const idx = this.hiddenColumns.indexOf(col);
      if (idx === -1) {
        this.hiddenColumns.push(col);
      } else {
        this.hiddenColumns.splice(idx, 1);
      }
    },
    toggleAllColumns() {
      if (this.areAllColumnsVisible) {
        this.hiddenColumns = this.tableColumns.map(col => col.name);
      } else {
        this.hiddenColumns = [];
      }
    },
    closeColumnMenu() {
      this.showColumnMenu = false;
    },
    closeInfoPanel() {
      this.showInfoPanel = false;
    },
    downloadTableCsv() {
      if (!this.filteredRows.length) return;
      const rows = this.filteredRows.map((item) => item.row);
      const csv = jsonToCsv(rows, this.visibleColumns);
      const layerItem = this.rawResults.find(r => r.layer && String(r.layer.id) === String(this.layerSelected));
      const layerName = (layerItem && layerItem.layer && layerItem.layer.name) || "resultados";
      const sanitizedName = layerName
        .normalize("NFD").replace(/[̀-ͯ]/g, "")
        .replace(/[^a-zA-Z0-9]+/g, "_");
      attributesTableService.triggerDownload(`${sanitizedName}.csv`, csv, "text/csv;charset=utf-8;");
    },
    async zoomToFeature(idx) {
      if (!this.rawResults || !this.rawResults.length) return;
      // La tabla concatena las features de TODOS los radios del mismo layer_id
      // (igual orden que arma resultsTable en el backend), así que hay que
      // recorrer todos los resultados que matcheen la capa, no solo el primero.
      const matchingItems = this.rawResults.filter(r => r.layer && String(r.layer.id) === String(this.layerSelected));
      if (!matchingItems.length) return;

      let feature = null;
      let remaining = idx;
      for (const item of matchingItems) {
        const layerRes = item.layer_results || {};
        const features = layerRes.features || layerRes.data || [];
        if (remaining < features.length) {
          feature = features[remaining];
          break;
        }
        remaining -= features.length;
      }

      if (!feature) return;

      let geometry = feature.geometry;

      if (!geometry) {
        const layer = matchingItems[0].layer;
        const attrs = feature.attributes || feature;
        const featureId = attrs.objectid !== undefined ? attrs.objectid : 
                         (attrs.OBJECTID !== undefined ? attrs.OBJECTID : 
                         (attrs.FID !== undefined ? attrs.FID : attrs.fid));
                         
        if (layer && layer.provider && layer.provider.name === "arcgis" && featureId !== undefined) {
            const baseUrl = layer.provider.url;
            const layerCodigo = layer.codigo || layer.id;
            let url = `${baseUrl}/${layerCodigo}/query`;
            if (baseUrl.toLowerCase().includes("imageserver")) {
                url = `${baseUrl}/query`;
            }
            
            const idField = attrs.objectid !== undefined ? 'objectid' : 
                           (attrs.OBJECTID !== undefined ? 'OBJECTID' : 
                           (attrs.FID !== undefined ? 'FID' : 'fid'));

            const params = new URLSearchParams({
                where: `${idField}=${featureId}`,
                returnGeometry: 'true',
                outFields: idField,
                outSR: '3857',
                f: 'json'
            });

            try {
                const response = await fetch(`${url}?${params.toString()}`).then(res => res.json());
                if (response.features && response.features.length > 0) {
                    geometry = response.features[0].geometry;
                }
            } catch (err) {
                console.error("Error fetching feature geometry:", err);
            }
        }
      }
      
      if (!geometry) return;
      
      const map = mapService.get();
      if (!map) return;
      
      if (geometry.rings || geometry.paths) {
        const shapeArray = geometry.rings || geometry.paths;
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
        shapeArray.forEach(ring => {
            ring.forEach(p => {
              if (p[0] < minX) minX = p[0];
              if (p[1] < minY) minY = p[1];
              if (p[0] > maxX) maxX = p[0];
              if (p[1] > maxY) maxY = p[1];
            });
        });
        
        const isLatLon = minX >= -180 && minX <= 180 && minY >= -90 && minY <= 90;
        let extent = [minX, minY, maxX, maxY];
        
        if (isLatLon) {
          const minP = fromLonLat([minX, minY]);
          const maxP = fromLonLat([maxX, maxY]);
          extent = [minP[0], minP[1], maxP[0], maxP[1]];
        }
        
        map.getView().fit(extent, { duration: 1000, padding: [100, 100, 100, 100], maxZoom: 18 });
      } else if (geometry.x && geometry.y) {
        const isLatLon = geometry.x >= -180 && geometry.x <= 180 && geometry.y >= -90 && geometry.y <= 90;
        let center = [geometry.x, geometry.y];
        
        if (isLatLon) {
          center = fromLonLat(center);
        }
        
        map.getView().animate({ center: center, zoom: 18, duration: 1000 });
      }
    }
  }
};
</script>

<style scoped>
.table-body {
  box-sizing: border-box;
  padding: 14px 16px 16px;
  min-height: 400px;
  color: var(--color-graphite, #2b3138);
  font-family: "Public Sans", system-ui, sans-serif;
}

.empty-selection {
  min-height: 380px;
  padding: 18px;
  background: var(--color-white, #fff);
  border: 1px dashed var(--color-line, #dbe1e6);
  border-radius: 6px;
  color: var(--color-ghost, #8a929a);
  text-align: center;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.45;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.custom-table {
  font-size: 0.85rem;
  background: var(--color-white, #fff);
}

.custom-table {
  width: 100%;
  table-layout: auto;
  border-collapse: collapse;
}

.custom-table th, .custom-table td {
  padding: 9px 12px;
  vertical-align: middle;
  border: 0 !important;
  border-bottom: 1px solid var(--color-line, #dbe1e6) !important;
}

.custom-table th {
  color: var(--color-navy, #0e2a40);
  background: var(--color-mist, #f2f4f6) !important;
  border-bottom: 1px solid var(--color-line, #dbe1e6) !important;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

.custom-table tbody tr:nth-of-type(odd) {
  background: var(--color-white, #fff) !important;
}

.custom-table tbody tr:nth-of-type(even) {
  background: #f8f9fa !important;
}

.custom-table tbody tr:hover {
  background: var(--color-orange-light, #fce8d5) !important;
}

.clickable-row {
  cursor: pointer;
  transition: all 0.2s ease;
}

.clickable-row:active {
  background: var(--color-fog, #dbe1e6) !important;
}

.kpi-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;

  margin-bottom: 14px;
}

.kpi-card {
  flex: 1 1 120px;

  display: flex;
  flex-direction: column;
  gap: 2px;

  padding: 7px 10px;

  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-left: 3px solid var(--color-orange, #e8730c);
  border-radius: 6px;
}

.kpi-value {
  color: var(--color-navy, #0e2a40);
  font-size: 1.05rem;
  font-weight: 700;
}

.kpi-label {
  color: var(--color-ghost, #8a929a);
  font-size: 0.68rem;
  line-height: 1.3;
}

.table-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.table-search-input {
  flex: 1 1 auto;
  min-height: 34px;
  padding: 7px 11px;

  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 4px;

  color: var(--color-graphite, #2b3138);
  font-family: inherit;
  font-size: 0.8rem;

  transition: border-color 0.2s ease, outline-color 0.2s ease;
}

.table-search-input:hover {
  border-color: var(--color-steel, #3b6e94);
}

.table-search-input:focus {
  border-color: var(--color-steel, #3b6e94);
  outline: 2px solid var(--color-fog, #dbe1e6);
  outline-offset: 1px;
}

.info-panel {
  width: max-content;
  max-width: 320px;
}

.info-panel-subtitle {
  margin: 10px 0 6px;
  color: var(--color-navy, #0e2a40);
  font-size: 0.78rem;
  font-weight: 700;
}

.info-panel-subtitle:first-child {
  margin-top: 0;
}

.info-panel-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.info-panel-table td {
  padding: 5px 4px;
  border-bottom: 1px solid var(--color-line, #dbe1e6);
  color: var(--color-graphite, #2b3138);
}

.info-panel-table td:last-child {
  text-align: right;
  font-weight: 700;
  color: var(--color-navy, #0e2a40);
}

.info-panel-empty {
  margin: 0;
  color: var(--color-ghost, #8a929a);
  font-size: 0.78rem;
}

.column-toggler-container {
  position: relative;
}

.column-toggle-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 34px;
  padding: 7px 11px;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 4px;
  color: var(--color-graphite, #2b3138);
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.column-toggle-btn:hover {
  background: var(--color-fog, #dbe1e6);
  border-color: var(--color-steel, #3b6e94);
  color: var(--color-navy, #0e2a40);
}

.column-toggle-btn:focus-visible {
  outline: 2px solid var(--color-orange, #e8730c);
  outline-offset: 2px;
}

.column-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  overflow: hidden;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
  box-shadow: 0 8px 22px rgba(14, 42, 64, 0.16);
  z-index: 1000;
  width: max-content;
  max-width: 250px;
}

.column-dropdown-header {
  padding: 9px 12px;
  background: var(--color-navy, #0e2a40);
  border-bottom: 1px solid var(--color-line, #dbe1e6);
  color: var(--color-white, #fff);
  font-size: 0.78rem;
  font-weight: 700;
}

.column-dropdown-body {
  max-height: 250px;
  overflow-y: auto;
  padding: 9px 10px;
}

.column-checkbox {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 5px 2px;
  color: var(--color-graphite, #2b3138);
  font-size: 0.78rem;
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.column-checkbox--all {
  margin-bottom: 4px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--color-line, #dbe1e6);
  color: var(--color-navy, #0e2a40);
  font-weight: 700;
}

.column-checkbox input {
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  margin: 0;
  accent-color: var(--color-orange, #e8730c);
}

.table-responsive {
  max-height: none;
  overflow-y: auto;
  overflow-x: auto;
  background: var(--color-white, #fff);
  border: 1px solid var(--color-line, #dbe1e6);
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(14, 42, 64, 0.06);
}

@media (max-width: 700px) {
  .table-body {
    padding-right: 10px;
    padding-left: 10px;
  }
}
</style>
